import type { FormResponse } from '@piccolohealth/pbs-common';
import { ChakraV3, dataAttr, FancyDate, ScrollArea } from '@piccolohealth/ui';
import { inflection, P } from '@piccolohealth/util';
import React from 'react';
import { FormResponseStatusTag } from './FormResponseStatusTag';

interface FormResponsesSidebarProps {
  formResponses: FormResponse[];
  activeResponseId: string | null;
  setActiveResponseId: (responseId: string) => void;
}

export const FormResponsesSidebar = (props: FormResponsesSidebarProps) => {
  const { formResponses, activeResponseId, setActiveResponseId } = props;

  const activeResponse = React.useMemo(() => {
    return formResponses.find((response) => response.id === activeResponseId);
  }, [formResponses, activeResponseId]);

  const content = P.run(() => {
    if (P.isEmpty(formResponses)) {
      return (
        <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
          No responses found
        </ChakraV3.Text>
      );
    }

    return (
      <>
        <ChakraV3.Text fontSize='xs' fontWeight='bold' color='secondary'>
          Responses
        </ChakraV3.Text>
        <ScrollArea flexDir='column' overflowY='auto' h='full'>
          <ChakraV3.Stack pr={4}>
            {formResponses.map((formResponse) => (
              <ChakraV3.Button
                as={ChakraV3.HStack}
                key={formResponse.submissionId}
                data-active={dataAttr(activeResponse?.submissionId === formResponse.submissionId)}
                onClick={() => setActiveResponseId(formResponse.id)}
                variant='ghost'
                color='gray.600'
                size='sm'
                justifyContent='start'
                whiteSpace='initial'
                h='auto'
                px={3}
                py={2}
                rounded='md'
              >
                <ChakraV3.HStack w='full' align='start'>
                  <ChakraV3.Stack gap={1} w='full'>
                    <ChakraV3.HStack w='full'>
                      <ChakraV3.Text fontWeight='semibold'>
                        Response {formResponse.submissionId}
                      </ChakraV3.Text>
                      <ChakraV3.Spacer />
                      <FormResponseStatusTag status={formResponse.status} />
                    </ChakraV3.HStack>
                    <FancyDate
                      fontSize='xs'
                      color='secondary'
                      date={formResponse.createdAt}
                      showTooltip={false}
                      direction='row'
                    />
                  </ChakraV3.Stack>
                  <ChakraV3.Spacer />
                </ChakraV3.HStack>
              </ChakraV3.Button>
            ))}
          </ChakraV3.Stack>
        </ScrollArea>
        <ChakraV3.Separator />
        <ChakraV3.HStack pr={4}>
          <ChakraV3.Text fontSize='xs' whiteSpace='nowrap'>
            Latest {formResponses.length} {inflection.inflect('response', formResponses.length)}
          </ChakraV3.Text>
        </ChakraV3.HStack>
      </>
    );
  });

  return (
    <ChakraV3.Stack
      h='full'
      w='320px'
      gap={1}
      flexShrink={0}
      borderRightWidth='1px'
      borderRightColor='gray.200'
    >
      {content}
    </ChakraV3.Stack>
  );
};
